import { useContext, useCallback, useMemo } from 'react';
import { storeContext } from '../../provider/Provider';
import { yextActions } from '../../../store/yext-reducer';
import { 
  yextList, 
  yextProxyReport, 
  yextImportCsv 
} from '../services/yext.service';

const useYext = () => {
  const { state, dispatch } = useContext(storeContext);
  const { yext } = state;


  // Get state values from store
  const {
    modalOpen,
    snackbarOpen,
    snackbarMessage,
    isFetching,
    isFetchingLocations,
    isScanning,
    error,
    locations,
    currentLocation,
    scanResults,
    pagination,
    filters,
    sorting,
  } = yext;

  // Load locations
  const loadLocations = useCallback(async (params = {}) => {
    try {
      dispatch({
        type: yextActions.SET_IS_FETCHING_LOCATIONS,
        payload: { isFetchingLocations: true },
      });

      const response = await yextList(params);
      
      dispatch({
        type: yextActions.SET_LOCATIONS,
        payload: { locations: response.data.results || response.data },
      });

      // Update pagination if provided
      if (response.data.count !== undefined) {
        dispatch({
          type: yextActions.SET_PAGINATION,
          payload: {
            total: response.data.count,
            totalPages: Math.ceil(response.data.count / pagination.perPage),
          },
        });
      }
    } catch (err) {
      dispatch({
        type: yextActions.SET_ERROR,
        payload: { error: err.message },
      });
      
      dispatch({
        type: yextActions.SET_SNACKBAR,
        payload: {
          snackbarOpen: true,
          snackbarMessage: 'Failed to load locations',
        },
      });
    } finally {
      dispatch({
        type: yextActions.SET_IS_FETCHING_LOCATIONS,
        payload: { isFetchingLocations: false },
      });
    }
  }, [dispatch, pagination.perPage]);

  // Select location
  const selectLocation = useCallback((location) => {
    dispatch({
      type: yextActions.SET_CURRENT_LOCATION,
      payload: { currentLocation: location },
    });
  }, [dispatch]);

  // Scan location - returns HTML to be embedded in iframe
  const scanLocation = useCallback(async (locationId) => {
    try {
      dispatch({
        type: yextActions.SET_IS_SCANNING,
        payload: { isScanning: true },
      });

      const response = await yextProxyReport(locationId);
      
      // Store the HTML content directly for iframe embedding
      dispatch({
        type: yextActions.SET_SCAN_RESULTS,
        payload: { scanResults: response.data },
      });
      
      dispatch({
        type: yextActions.SET_SNACKBAR,
        payload: {
          snackbarOpen: true,
          snackbarMessage: 'Scan completed successfully',
        },
      });
    } catch (err) {
      dispatch({
        type: yextActions.SET_ERROR,
        payload: { error: err.message },
      });
      
      dispatch({
        type: yextActions.SET_SNACKBAR,
        payload: {
          snackbarOpen: true,
          snackbarMessage: 'Failed to scan location',
        },
      });
    } finally {
      dispatch({
        type: yextActions.SET_IS_SCANNING,
        payload: { isScanning: false },
      });
    }
  }, [dispatch]);


  // Import CSV
  const importCSV = useCallback(async (file) => {
    try {
      dispatch({
        type: yextActions.SET_IS_FETCHING,
        payload: { isFetching: true },
      });

      await yextImportCsv(file);
      
      // Reload locations after import
      await loadLocations();
      
      dispatch({
        type: yextActions.SET_SNACKBAR,
        payload: {
          snackbarOpen: true,
          snackbarMessage: 'CSV imported successfully',
        },
      });
    } catch (err) {
      dispatch({
        type: yextActions.SET_ERROR,
        payload: { error: err.message },
      });
      
      dispatch({
        type: yextActions.SET_SNACKBAR,
        payload: {
          snackbarOpen: true,
          snackbarMessage: 'Failed to import CSV',
        },
      });
      throw error;
    } finally {
      dispatch({
        type: yextActions.SET_IS_FETCHING,
        payload: { isFetching: false },
      });
    }
  }, [dispatch, loadLocations]);

  // Set filters
  const setFilters = useCallback((newFilters) => {
    dispatch({
      type: yextActions.SET_FILTERS,
      payload: { filters: newFilters },
    });
  }, [dispatch]);

  // Clear filters
  const clearFilters = useCallback(() => {
    dispatch({
      type: yextActions.CLEAR_FILTERS,
    });
  }, [dispatch]);

  // Set sorting
  const setSorting = useCallback((newSorting) => {
    dispatch({
      type: yextActions.SET_SORTING,
      payload: { sorting: newSorting },
    });
  }, [dispatch]);

  // Set pagination
  const setPagination = useCallback((newPagination) => {
    dispatch({
      type: yextActions.SET_PAGINATION,
      payload: newPagination,
    });
  }, [dispatch]);

  // Close snackbar
  const closeSnackbar = useCallback(() => {
    dispatch({
      type: yextActions.CLOSE_SNACKBAR,
    });
  }, [dispatch]);


  // Set modal open
  const setModalOpen = useCallback((open) => {
    dispatch({
      type: yextActions.SET_MODAL_OPEN,
      payload: { modalOpen: open },
    });
  }, [dispatch]);

  // Memoized filtered locations
  const filteredLocations = useMemo(() => {
    if (!locations || locations.length === 0) return [];
    
    const currentWebsite = state.websiteSwitch?.currentWebsite || '';
    
    return locations.filter(location => {
      // Filter by current website domain
      const websiteMatch = !currentWebsite || 
        location.client_website?.includes(currentWebsite);
      
      // Apply other filters
      const nameMatch = !filters.name || 
        location.name?.toLowerCase().includes(filters.name.toLowerCase());
      const addressMatch = !filters.address || 
        location.address?.toLowerCase().includes(filters.address.toLowerCase());
      const phoneMatch = !filters.phone || 
        location.phone?.includes(filters.phone);
      const statusMatch = !filters.status || 
        location.status?.toLowerCase().includes(filters.status.toLowerCase());
      
      return websiteMatch && nameMatch && addressMatch && phoneMatch && statusMatch;
    });
  }, [locations, filters, state.websiteSwitch?.currentWebsite]);

  return {
    // State
    modalOpen,
    snackbarOpen,
    snackbarMessage,
    isFetching,
    isFetchingLocations,
    isScanning,
    error,
    locations: filteredLocations,
    currentLocation,
    scanResults,
    pagination,
    filters,
    sorting,
    
    // Actions
    loadLocations,
    selectLocation,
    scanLocation,
    importCSV,
    setFilters,
    clearFilters,
    setSorting,
    setPagination,
    closeSnackbar,
    setModalOpen,
  };
};


export default useYext;